import * as React from "react";
import { useState } from "react";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";
import { DefaultizedPieValueType } from "@mui/x-charts/models";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { GraphDataItem } from "./DashboardTypes";

const sizing = {
  margin: { right: 2 },
  width: 200,
  height: 200,
  legend: { hidden: true },
};

interface UploadPieChartProps {
  data: GraphDataItem[];
}

const getArcLabel = (params: DefaultizedPieValueType) => `${params.value}`;

const StyledText = styled("text")(() => ({
  fill: "#000",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 18,
  fontWeight: "bold",
}));

function CenterLabel({ total }: { total: number }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <StyledText x={left + width / 2} y={top + height / 2}>
      <tspan x={left + width / 2} dy="-0.6em">
        {total}
      </tspan>
      <tspan x={left + width / 2} dy="1.2em" fontSize="16">
        uploads
      </tspan>
    </StyledText>
  );
}

const UploadPieChart: React.FC<UploadPieChartProps> = ({ data }) => {
  const [selected, setSelected] = useState(() => data[0]?.section || "");
  const sectionData = data.find((item) => item.section === selected);

    React.useEffect(() => {
    if (data.length > 0 && !selected) {
      setSelected(data[0].section);
    }
  }, [data]);
  

  const chartData = sectionData
    ? [
        {
          label: "Approved",
          value: sectionData.sum_of_approved_count,
          color: "#85AF85",
       
        },
        {
          label: "Pending",
          value: sectionData.sum_of_pending_count,
          color: "#E06B6B",
        },
      ].filter((item) => item.value > 0)
    : [];

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: "20px",
          p: "20px",
          pb: "0px",
        }}
      >
        <Typography
          sx={{ p: 1, fontSize: "16px", fontWeight: "600", maxWidth: "50%" }}
        >
          Upload Summary
        </Typography>
        <Box sx={{ p: 2 }}>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            style={{ padding: "0.5rem" }}
          >
            {data.map((item) => (
              <option key={item.section} value={item.section}>
                {item.section}
              </option>
            ))}
          </select>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: "20px",
          p: "20px",
        }}
      >
        <Box>
          <PieChart
            series={[
              {
                outerRadius: 80,
                innerRadius: 40,
                data: chartData,
                arcLabel: getArcLabel,
              
              },
            ]}
            sx={{
              [`& .${pieArcLabelClasses.root}`]: {
                fill: "#000",
                textAnchor: "middle",
                dominantBaseline: "central",
                fontSize: 14,
                fontWeight: "bold",

              },
            }}
        
            {...sizing}
          >
            <CenterLabel total={total} />
           
          </PieChart>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            justifyContent: "end",
          }}
        >
         {chartData.map((item) => (
  <Box
    key={item.label}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
    }}
  >
    <Box
      sx={{
        width: "15px",
        height: "15px",
        borderRadius: "50%",
        backgroundColor: item.color, // match slice color
      }}
    />
    <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
      Count of {item.label}: {item.value}
    </Typography>
  </Box>
))}
        </Box>
      </Box>
    </Box>
  );
};

export default UploadPieChart;
